import * as React from 'react';
import { graphql } from 'react-apollo';

import { branch, compose, withProps } from 'recompose';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error TS7016 Untyped import http://go.dkandu.me/strict-ts-migration#TS7016
import getPropsFromPromise from 'js/lib/getPropsFromPromise';
import requestCountry from 'js/lib/requestCountry';
import waitFor from 'js/lib/waitFor';

import { useGetSubscriptionTiersProductPrices } from 'bundles/coursera-plus-common/hooks/useGetSubscriptionTiersProductPrices';
import {
  previewPrePurchaseUpsellVariant,
  useIsEligibleForCourseraPlusUpsell,
} from 'bundles/coursera-plus-common/utils/prePurchaseUpsellExperimentUtils';
import { showPrePurchaseUpsellFlow } from 'bundles/coursera-plus-common/utils/prePurchaseUpsellUtils';
import withCourseraPlusProductEligibilityCheck from 'bundles/coursera-plus-common/utils/withCourseraPlusProductEligibilityCheck';
import type { EligibilityCheckProps } from 'bundles/coursera-plus-common/utils/withCourseraPlusProductEligibilityCheck';
import withCourseraPlusProductOwnerships from 'bundles/coursera-plus-common/utils/withCourseraPlusProductOwnerships';
import type { PropsFromCourseraPlusProductOwnerships } from 'bundles/coursera-plus-common/utils/withCourseraPlusProductOwnerships';
import { getFormattedCourseDataFromQuery } from 'bundles/enroll-common/components/xdp/enrollSectionUtils';
import withEnrollment from 'bundles/enroll-common/components/xdp/withEnrollment';
import type { PropsFromWithEnrollment } from 'bundles/enroll-common/components/xdp/withEnrollment';
import { useGetPartnerNamesByIds } from 'bundles/enroll-common/hooks/useGetPartnerNamesByIds';
import {
  getAvailableGroups,
  getAvailableInvitedPrograms,
  getAvailablePrograms,
} from 'bundles/enroll-common/lib/apiClient';
import type { GroupsData, InvitedProgramsData, ProgramsData } from 'bundles/enroll-common/lib/apiClient';
import GetCourseForXdpEnrollCta from 'bundles/enroll-common/queries/GetCourseForXdpEnrollCtaQuery.graphql';
import type {
  GetCourseForXdpEnrollCtaQuery,
  GetCourseForXdpEnrollCtaQueryVariables,
} from 'bundles/enroll-common/queries/__generated__/GetCourseForXdpEnrollCtaQuery';
import type { FormattedCourse } from 'bundles/enroll-common/types/enrollSectionTypes';
import { withCoursesModulesDuration } from 'bundles/enroll-modal/components/non-recurring/withS12nProductInfo';
import PageDataContext from 'bundles/enroll-modal/data/PageDataContext';
import { useGetCourseraPlusMonthlyUpsellProductPrice } from 'bundles/enroll-modal/utils/courseraPlusPrePurchaseUpsellUtils';
import type { FormattedGraphqlProductOwnership } from 'bundles/entitlements-common/utils/entitlementProductOwnershipUtils';
import {
  withProductOwnerships,
  withSpecializationProductOwnershipsId,
} from 'bundles/entitlements-common/utils/withProductOwnerships';
import paymentsExperiments from 'bundles/epic/clients/payments';
// eslint-disable-next-line @coursera/custom-rules/no-barrel-file-imports
import Naptime from 'bundles/naptimejs';
import CourseTypeMetadataV1, { CourseType } from 'bundles/naptimejs/resources/courseTypeMetadata.v1';
import CoursesV1 from 'bundles/naptimejs/resources/courses.v1';
import OnDemandSpecializationsV1 from 'bundles/naptimejs/resources/onDemandSpecializations.v1';
import S12nDerivativesV1 from 'bundles/naptimejs/resources/s12nDerivatives.v1';
import {
  getS12nSubscriptionOwnableProductFromOwnableProducts,
  isS12nDerivativesToOwnableProductsEnabled,
  withFindOwnableProductsByUnderlyingProductItemId,
} from 'bundles/ownable-products-common/hooks/useFindOwnableProducts';
import type {
  OwnableProductByUnderlyingProductItemId,
  PropsFromFindByUnderlyingProductItemId,
} from 'bundles/ownable-products-common/hooks/useFindOwnableProducts';
import { useGetProductPrices } from 'bundles/ownable-products-common/hooks/useGetProductPrices';
import type { ProductPrices } from 'bundles/ownable-products-common/hooks/useGetProductPrices';
import type { ProductType as IProductType } from 'bundles/payments-common/common/ProductType';
import ProductType from 'bundles/payments-common/common/ProductType';
import type { PropsFromWithPromotionInfo } from 'bundles/promotions/components/withPromotionInfo';
import withPromotionInfo from 'bundles/promotions/components/withPromotionInfo';

type PropsFromCaller = {
  isNotRequired?: boolean;
  onSdp?: boolean;
  courseId?: string;
  s12nId?: string;
  selectedS12nId?: string;
  // Added for freemium model rollout GA preload of s12n prepaid modal when a learner clicks the freemium upgrade cta
  preloadPrepaidModal?: boolean;
};

type PropsFromWithProps = {
  isSpecialization: boolean;
};

type PropsToWithCourseEnrollModalData = Required<PropsFromWithEnrollment> & {
  courseId: string;
  selectedS12nId?: string;
  underlyingProductItemId?: string;
};

type PropsFromGraphqlCourseOptional = {
  course?: FormattedCourse;
};

type PropsFromGraphqlCourse = Required<PropsFromGraphqlCourseOptional>;

type PropsFromNaptimeCourseTypeMetadata = {
  courseTypeMetadataWithVersion: CourseTypeMetadataV1;
};

type PropsFromNaptimeS12nAndS12nDerivatives = {
  s12n?: OnDemandSpecializationsV1;
  s12nDerivatives?: S12nDerivativesV1;
};

type PropsFromNaptimeS12nMultiGet = {
  s12ns: OnDemandSpecializationsV1[];
};

type PropsFromNaptimeCourseMultiGet = {
  s12nCourses: CoursesV1[];
};

type PropsFromProgramAndGroupData = GroupsData & ProgramsData & InvitedProgramsData;

type PropsFromWithCourseEnrollS12nSelectionModalData = PropsFromNaptimeS12nMultiGet & PropsFromNaptimeCourseMultiGet;

type PropsFromS12nSubscriptionOwnableProduct = {
  s12nSubscriptionOwnableProduct?: OwnableProductByUnderlyingProductItemId;
};

type PropsFromOwnableProducts = {
  ownableProducts: OwnableProductByUnderlyingProductItemId[];
} & PropsFromS12nSubscriptionOwnableProduct;

type PropsFromWithCourseEnrollModalData = PropsFromGraphqlCourse &
  PropsFromNaptimeCourseTypeMetadata &
  PropsFromNaptimeS12nAndS12nDerivatives &
  PropsFromWithCourseEnrollS12nSelectionModalData &
  PropsFromCourseraPlusProductOwnerships &
  PropsFromOwnableProducts;

type PropsToWithS12nEnrollModalData = Required<PropsFromWithEnrollment> & {
  courseId: string;
  s12nId: string;
  underlyingProductItemId?: string;
};

type PropsFromNaptimeS12n = {
  s12n: OnDemandSpecializationsV1;
};

type PropsFromNaptimeS12nAndProductOwnerships = PropsFromNaptimeS12n & {
  productOwnerships: FormattedGraphqlProductOwnership;
};

type PropsFromNaptimeS12nDerivatives = {
  s12nDerivatives: S12nDerivativesV1;
};

type PropsFromWithS12nEnrollModalData = PropsFromNaptimeS12nAndProductOwnerships &
  PropsFromGraphqlCourse &
  PropsFromNaptimeS12nDerivatives &
  PropsFromOwnableProducts;

export type PropsToComponent = PropsFromCaller &
  PropsFromWithProps &
  Required<PropsFromWithEnrollment> &
  PropsFromWithCourseEnrollModalData &
  PropsFromWithS12nEnrollModalData &
  PropsFromWithPromotionInfo &
  EligibilityCheckProps & {
    children?: React.ReactNode;
  };

// All course enrollments that allow individual course purchase need course product price data
// This includes usage in the S12nBulkPayEnrollModal, so need this data regardless of `onSdp` or not
const useGetCourseProductPrice = ({ enrollmentAvailableChoices, courseId, course }: PropsToComponent) => {
  const productItemId = courseId ?? course.id;
  const shouldGetProductPrice = enrollmentAvailableChoices.canPurchaseSingleCourse && productItemId;
  const { productPrices } = useGetProductPrices({
    products: [
      {
        productType: ProductType.VERIFIED_CERTIFICATE,
        productItemId,
      },
    ],
    skip: !shouldGetProductPrice,
  });
  const productPrice = productPrices?.[0];

  return {
    productPrice,
    shouldGetProductPrice,
  };
};

// All s12n enrollments that allow bulk pay purchase need s12n bulk product price data
// This includes usage in the CourseEnrollChoiceDescription, so need this data regardless of `onSdp` or not
const useGetS12nProductPrice = ({ enrollmentAvailableChoices, s12nId, course }: PropsToComponent) => {
  const productItemId = s12nId ?? course?.s12nIds?.[0];
  const shouldGetS12nProductPrice = enrollmentAvailableChoices.canBulkPaySpecialization && productItemId;
  const { productPrices: s12nProductPrices } = useGetProductPrices({
    products: productItemId
      ? [
          {
            productType: ProductType.SPECIALIZATION,
            productItemId,
          },
        ]
      : [],
    skip: !shouldGetS12nProductPrice,
  });
  const s12nProductPrice = s12nProductPrices?.[0];

  return {
    s12nProductPrice,
    shouldGetS12nProductPrice,
  };
};

// All s12n enrollments that allow s12n prepaid purchase need s12n prepaid product price data
// This includes usage in the NonRecurringChoice, so need this data regardless of `onSdp` or not
const useGetS12nPrepaidProductPrice = ({ enrollmentAvailableChoices, selectedS12nId }: PropsToComponent) => {
  let availablePrepaid;

  // price call is skipped in Mix and Match until the learner selects the s12n
  if (enrollmentAvailableChoices.isMixAndMatch && selectedS12nId) {
    availablePrepaid = enrollmentAvailableChoices?.getS12nPrepaidProducts(selectedS12nId);
  } else if (!enrollmentAvailableChoices.isMixAndMatch) {
    availablePrepaid = enrollmentAvailableChoices?.getS12nPrepaidProducts();
  }

  const products =
    availablePrepaid?.map(({ productItemId }) => ({
      productType: ProductType.SPECIALIZATION_PREPAID,
      productItemId,
    })) ?? [];

  const shouldGetS12nPrepaidProductPrice =
    enrollmentAvailableChoices.canEnrollThroughS12nPrepaid && products.length !== 0;

  const { productPrices: s12nPrepaidProductPrices } = useGetProductPrices({
    products,
    skip: !shouldGetS12nPrepaidProductPrice,
  });

  // Returns the most inexpensive s12n prepaid product price
  const sortedProductPrices = s12nPrepaidProductPrices?.sort(
    (priceA: ProductPrices, priceB: ProductPrices) => priceA.amount - priceB.amount
  );
  const s12nPrepaidProductPrice = sortedProductPrices?.[0];

  return {
    s12nPrepaidProductPrices,
    s12nPrepaidProductPrice,
    shouldGetS12nPrepaidProductPrice,
  };
};

const useGetCourseraPlusPrepaidProductPrice = ({ enrollmentAvailableChoices, selectedS12nId }: PropsToComponent) => {
  let availablePrepaid;

  // price call is skipped in Mix and Match until the learner selects the s12n
  if (enrollmentAvailableChoices.isMixAndMatch && selectedS12nId) {
    availablePrepaid = enrollmentAvailableChoices?.getCourseraPlusPrepaidProducts(selectedS12nId);
  } else if (!enrollmentAvailableChoices.isMixAndMatch) {
    availablePrepaid = enrollmentAvailableChoices?.getCourseraPlusPrepaidProducts();
  }

  const products =
    availablePrepaid?.map((prepaid) => ({
      productItemId: prepaid.productItemId,
      productType: prepaid.productType as IProductType,
    })) ?? [];

  // We previously used epic to apply country targeting.
  // Now that we no longer need epic, we'll do it in code.
  const shouldGetCourseraPlusPrepaidProductPrice =
    requestCountry.get() === 'IN' && enrollmentAvailableChoices.canPurchaseThroughCourseraPlus && products.length !== 0;

  const { productPrices: courseraPlusPrepaidProductPrices } = useGetProductPrices({
    products,
    skip: !shouldGetCourseraPlusPrepaidProductPrice,
  });

  // Returns the most inexpensive Coursera Plus prepaid product price
  const sortedProductPrices = courseraPlusPrepaidProductPrices?.sort((priceA, priceB) => priceA.amount - priceB.amount);
  const courseraPlusPrepaidProductPrice = sortedProductPrices?.[0];

  return {
    courseraPlusPrepaidProductPrices,
    courseraPlusPrepaidProductPrice,
    shouldGetCourseraPlusPrepaidProductPrice,
  };
};

const DataProvider = ({ children, ...data }: PropsToComponent) => {
  const {
    isSpecialization,
    ownsCourseraLite,
    s12n,
    course,
    courseTypeMetadataWithVersion,
    enrollmentAvailableChoices,
    isCourseraPlusEligibleProduct,
  } = data;
  const partnerIds = isSpecialization && s12n ? s12n.partnerIds : course?.partnerIds;

  // If learner owns Lite and is enrolled in a course part of Plus, retrieve pricing for the Plus upgrade modal
  const shouldGetTiersPrices =
    ownsCourseraLite && enrollmentAvailableChoices.isEnrolled && isCourseraPlusEligibleProduct;

  const { courseraPlusMonthlyProductPrice } = useGetSubscriptionTiersProductPrices({
    skip: !shouldGetTiersPrices,
  });

  const { productPrice, shouldGetProductPrice } = useGetCourseProductPrice(data);
  const { s12nProductPrice, shouldGetS12nProductPrice } = useGetS12nProductPrice(data);
  const { s12nPrepaidProductPrices, s12nPrepaidProductPrice, shouldGetS12nPrepaidProductPrice } =
    useGetS12nPrepaidProductPrice(data);
  const {
    courseraPlusPrepaidProductPrices,
    courseraPlusPrepaidProductPrice,
    shouldGetCourseraPlusPrepaidProductPrice,
  } = useGetCourseraPlusPrepaidProductPrice(data);

  const prePurchaseUpsellVariant = previewPrePurchaseUpsellVariant();
  const isPrePurchaseUpsellEligible = useIsEligibleForCourseraPlusUpsell({
    s12nId: s12n?.id,
    courseId: course?.id,
    skip: !(['1', '2'].includes(prePurchaseUpsellVariant) && enrollmentAvailableChoices?.canSubscribeToCourseraPlus),
  });
  const { courseraPlusMonthlyUpsellProductPrice } = useGetCourseraPlusMonthlyUpsellProductPrice({
    skip: !isPrePurchaseUpsellEligible,
  });

  const prices = {
    courseraPlusMonthlyProductPrice,
    ...(productPrice ? { productPrice } : {}),
    ...(s12nProductPrice ? { s12nProductPrice } : {}),
    ...(s12nPrepaidProductPrice ? { s12nPrepaidProductPrices, s12nPrepaidProductPrice } : {}),
    ...(courseraPlusPrepaidProductPrice ? { courseraPlusPrepaidProductPrices, courseraPlusPrepaidProductPrice } : {}),
    ...(courseraPlusMonthlyUpsellProductPrice ? { courseraPlusMonthlyUpsellProductPrice } : {}),
  };

  // Project here means either Guided Projects or Self-Paced Projects
  const isProject = [CourseType.PROJECT, CourseType.RHYME_PROJECT].includes(
    courseTypeMetadataWithVersion?.courseTypeMetadata?.typeName
  );

  // If the course is in multiple s12ns, we need the partner data in the s12n selection modal
  const isCourseInMultipleS12ns = course?.s12nIds && course?.s12nIds?.length > 1;

  // If its a standalone course
  const isStandaloneCourse = course?.s12nIds && course?.s12nIds?.length === 0;

  // Show partners in Mix and Match Modal flow or Standalone Course in pre-purchase upsell enrollment flow.
  // Otherwise, show in Project Enroll Modal.
  const shouldShowPartners = isCourseInMultipleS12ns || (showPrePurchaseUpsellFlow() ? isStandaloneCourse : isProject);

  const { partners, loading: loadingPartners } = useGetPartnerNamesByIds({
    partnerIds: partnerIds ?? [],
    skip: !shouldShowPartners,
  });

  if (
    loadingPartners ||
    (shouldGetProductPrice && !productPrice) ||
    (shouldGetS12nProductPrice && !s12nProductPrice) ||
    (shouldGetS12nPrepaidProductPrice && !s12nPrepaidProductPrice) ||
    (shouldGetCourseraPlusPrepaidProductPrice && !courseraPlusPrepaidProductPrice)
  ) {
    return null;
  }

  return <PageDataContext.Provider value={{ ...data, ...prices, partners }}>{children}</PageDataContext.Provider>;
};

const withCourseEnrollS12nSelectionModalData = compose<
  PropsFromWithCourseEnrollS12nSelectionModalData,
  React.PropsWithChildren<PropsToWithCourseEnrollModalData>
>(
  Naptime.createContainer<PropsFromNaptimeS12nMultiGet, PropsToWithCourseEnrollModalData>(
    ({ enrollmentAvailableChoices }) => ({
      s12ns: OnDemandSpecializationsV1.multiGet(enrollmentAvailableChoices.enrollmentS12nIds, {
        fields: ['id', 'name', 'courseIds', 'productVariant'],
      }),
    })
  ),
  waitFor<PropsFromNaptimeS12nMultiGet>(({ s12ns }) => s12ns?.length > 0),
  Naptime.createContainer<PropsFromNaptimeCourseMultiGet, PropsFromNaptimeS12nMultiGet>(({ s12ns }) => {
    const courseIds = s12ns.reduce((allCourseIds, s12n) => allCourseIds.concat(s12n.courseIds), [] as string[]);
    return {
      s12nCourses: CoursesV1.multiGet(courseIds, {
        fields: ['id', 'name'],
        params: {
          showHidden: true,
        },
      }),
    };
  }),
  waitFor<PropsFromNaptimeCourseMultiGet>(({ s12nCourses }) => s12nCourses?.length > 0)
);

const withProgramAndGroupData = compose<
  PropsFromProgramAndGroupData,
  React.PropsWithChildren<PropsFromCaller & Required<PropsFromWithEnrollment>>
>(
  getPropsFromPromise(
    ({ enrollmentAvailableChoices, courseId, s12nId, onSdp }: Required<PropsFromCaller & PropsFromWithEnrollment>) => {
      const options = onSdp ? { s12nId } : { courseId };
      const promises = [
        enrollmentAvailableChoices.canEnrollThroughProgram && getAvailablePrograms(options),
        enrollmentAvailableChoices.canEnrollThroughProgramInvitation && getAvailableInvitedPrograms(options),
        enrollmentAvailableChoices.canEnrollThroughGroup && getAvailableGroups(options),
        // Put an empty promise in place of any falsy values / promises that won't be called
      ].map((promise) => promise || Promise.resolve({}));

      // Request all promises concurrently and then flatten all the returned data
      return Promise.all(promises).then(([programsData, invitedProgramsData, groupsData]) => ({
        ...programsData,
        ...invitedProgramsData,
        ...groupsData,
      }));
    }
  )
);

const withCourseEnrollModalData = compose<
  PropsFromWithCourseEnrollModalData,
  React.PropsWithChildren<PropsToWithCourseEnrollModalData>
>(
  graphql<
    PropsToWithCourseEnrollModalData,
    GetCourseForXdpEnrollCtaQuery,
    GetCourseForXdpEnrollCtaQueryVariables,
    PropsFromGraphqlCourseOptional
  >(GetCourseForXdpEnrollCta, {
    options: ({ courseId }) => ({ variables: { id: courseId! } }),
    props: ({ data }) => ({ course: getFormattedCourseDataFromQuery(data)?.course }),
  }),
  waitFor<PropsFromGraphqlCourse>(({ course }) => !!course),
  Naptime.createContainer<PropsFromNaptimeCourseTypeMetadata, PropsToWithCourseEnrollModalData>(({ courseId }) => ({
    courseTypeMetadataWithVersion: CourseTypeMetadataV1.get(courseId),
  })),
  // Retrieve s12n if user can't enroll in multiple s12ns
  // Or can enroll in multiple s12ns but has already made a selection
  branch<PropsToWithCourseEnrollModalData>(
    ({ enrollmentAvailableChoices, selectedS12nId }) => {
      const { isMixAndMatch } = enrollmentAvailableChoices;
      return !isMixAndMatch || (isMixAndMatch && Boolean(selectedS12nId));
    },
    Naptime.createContainer<
      PropsFromNaptimeS12nAndS12nDerivatives,
      PropsToWithCourseEnrollModalData & PropsFromNaptimeCourseTypeMetadata
    >(({ enrollmentAvailableChoices, selectedS12nId }) => {
      let enrollmentS12nId;
      if (enrollmentAvailableChoices.isMixAndMatch) {
        enrollmentS12nId = selectedS12nId;
      } else if (enrollmentAvailableChoices.canSubscribeToS12n) {
        const s12nIds = enrollmentAvailableChoices.subscriptionEnrollmentS12nIds;
        enrollmentS12nId = s12nIds?.[0];
      } else if (enrollmentAvailableChoices.canEnrollThroughS12nPrepaid) {
        const s12nIds = enrollmentAvailableChoices.prepaidEnrollmentS12nIds;
        enrollmentS12nId = s12nIds?.[0];
      } else if (enrollmentAvailableChoices.isPreEnrolled) {
        const s12nIds = enrollmentAvailableChoices.preEnrolledS12nIds;
        enrollmentS12nId = s12nIds?.[0];
      } else if (
        enrollmentAvailableChoices.isSpecializationSubscribed ||
        enrollmentAvailableChoices.canEnrollThroughCourseraPlus
      ) {
        enrollmentS12nId = enrollmentAvailableChoices.specializationId;
      }

      // If enrolling user into s12n, we need to grab the s12nId returned from enrollmentAvailableChoices
      // to ensure we're displaying the same s12n that will show up on checkout
      // 'productVariant' and 'partnerIds' are used in child components and need to be requested here due to Naptime caching
      if (enrollmentS12nId) {
        const baseData = {
          s12n: OnDemandSpecializationsV1.get(enrollmentS12nId, {
            fields: ['name', 'courseIds', 'productVariant', 'partnerIds', 'launchedAt'],
          }),
        };

        if (isS12nDerivativesToOwnableProductsEnabled()) {
          return {
            ...baseData,
            underlyingProductItemId: enrollmentS12nId,
          };
        } else {
          return {
            ...baseData,
            s12nDerivatives: S12nDerivativesV1.get(enrollmentS12nId, {
              fields: ['catalogPrice'],
            }),
          };
        }
      } else {
        return {};
      }
    })
  ),
  branch<PropsToWithCourseEnrollModalData>(
    ({ underlyingProductItemId }) => Boolean(underlyingProductItemId) && isS12nDerivativesToOwnableProductsEnabled(),
    compose(
      withFindOwnableProductsByUnderlyingProductItemId,
      withProps<PropsFromS12nSubscriptionOwnableProduct, PropsFromFindByUnderlyingProductItemId>(
        ({ ownableProducts }) => ({
          s12nSubscriptionOwnableProduct: getS12nSubscriptionOwnableProductFromOwnableProducts({ ownableProducts }),
        })
      )
    )
  ),
  branch<PropsToWithCourseEnrollModalData>(
    ({ enrollmentAvailableChoices, selectedS12nId }) => enrollmentAvailableChoices.isMixAndMatch && !selectedS12nId,
    withCourseEnrollS12nSelectionModalData
  ),
  // Retrieve Coursera Lite/Plus ownership if they are enrolled
  // because we need to know if they own Lite and need to see the upgrade modal to Plus
  branch<PropsToWithCourseEnrollModalData>(
    ({ enrollmentAvailableChoices }) => enrollmentAvailableChoices.isEnrolled,
    withCourseraPlusProductOwnerships()
  ),
  // If the learner owns Lite and is enrolled, determine if the course is part of Coursera Plus to decide if the upgrade modal to Plus should be shown.
  // The pre-purchase enrollment flow is only displayed for courses or s12ns that are part of Coursera Plus.
  branch<Required<PropsFromWithEnrollment & PropsFromCourseraPlusProductOwnerships>>(
    ({ enrollmentAvailableChoices, ownsCourseraLite }) =>
      (ownsCourseraLite && enrollmentAvailableChoices.isEnrolled) || showPrePurchaseUpsellFlow(),
    withCourseraPlusProductEligibilityCheck<PropsFromCaller>()
  )
);

const withS12nEnrollModalData = compose<
  PropsFromWithS12nEnrollModalData,
  React.PropsWithChildren<PropsToWithS12nEnrollModalData>
>(
  Naptime.createContainer<PropsFromNaptimeS12n, PropsToWithS12nEnrollModalData>(({ s12nId }) => ({
    s12n: OnDemandSpecializationsV1.get(s12nId, {
      fields: ['name', 'courseIds', 'productVariant', 'partnerIds', 'capstone', 'launchedAt'],
    }),
  })),
  withProps<{ underlyingProductItemId: string }, { s12nId: string }>(({ s12nId }) => ({
    underlyingProductItemId: s12nId,
  })),
  withSpecializationProductOwnershipsId,
  withProductOwnerships({ isNotRequired: true }),
  graphql<
    PropsFromNaptimeS12nAndProductOwnerships & PropsToWithS12nEnrollModalData,
    GetCourseForXdpEnrollCtaQuery,
    GetCourseForXdpEnrollCtaQueryVariables,
    PropsFromGraphqlCourseOptional
  >(GetCourseForXdpEnrollCta, {
    options: ({ courseId: courseIdOverride, s12n, productOwnerships }) => {
      let courseId = courseIdOverride;

      if (!courseId) {
        // productOwnerships courseIds is not ordered, but s12n courseIds *is* ordered
        // So go through the list of s12n courseIds sequentially and find the first one that is in unowned list
        const firstUnownedCourseId = s12n.courseIds.find((s12nCourseId) => {
          return productOwnerships.unownedCourses?.includes(s12nCourseId);
        });

        // Show course info for the specified courseId or the first unowned course in the s12n
        // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        courseId = firstUnownedCourseId ?? s12n.courseIds[0];
      }

      return { variables: { id: courseId } };
    },
    props: ({ data }) => ({ course: getFormattedCourseDataFromQuery(data)?.course }),
  }),
  waitFor<PropsFromGraphqlCourse>(({ course }) => !!course),
  branch<PropsToWithS12nEnrollModalData>(
    ({ enrollmentAvailableChoices }) => enrollmentAvailableChoices.canSubscribeToS12n,
    branch<PropsToWithS12nEnrollModalData>(
      () => isS12nDerivativesToOwnableProductsEnabled(),
      compose(
        withFindOwnableProductsByUnderlyingProductItemId,
        withProps<PropsFromS12nSubscriptionOwnableProduct, PropsFromFindByUnderlyingProductItemId>(
          ({ ownableProducts }) => ({
            s12nSubscriptionOwnableProduct: getS12nSubscriptionOwnableProductFromOwnableProducts({ ownableProducts }),
          })
        )
      ),
      Naptime.createContainer<PropsFromNaptimeS12nDerivatives, PropsFromNaptimeS12nAndProductOwnerships>(
        ({ s12n }) => ({
          s12nDerivatives: S12nDerivativesV1.get(s12n.id, {
            fields: ['catalogPrice'],
          }),
        })
      )
    )
  )
);

export default compose<PropsToComponent, React.PropsWithChildren<PropsFromCaller>>(
  withProps<PropsFromWithProps, PropsFromCaller>(({ onSdp }) => ({
    isSpecialization: !!onSdp,
  })),
  withEnrollment(),
  waitFor<PropsFromWithEnrollment>(({ enrollmentAvailableChoices }) => !!enrollmentAvailableChoices),
  branch<PropsFromCaller>(({ onSdp }) => !onSdp, withCourseEnrollModalData),
  branch<PropsFromCaller>(({ onSdp }) => Boolean(onSdp), withS12nEnrollModalData),
  // All course (project, Guided Project, standard course) and s12n subscription purchases need promotion info
  branch<Required<PropsFromWithEnrollment>>(
    ({ enrollmentAvailableChoices }) =>
      enrollmentAvailableChoices.canPurchaseSingleCourse || enrollmentAvailableChoices.canSubscribeToS12n,
    branch<PropsFromCaller>(({ isNotRequired }) => !!isNotRequired, withPromotionInfo(true), withPromotionInfo(false))
  ),
  branch<Required<PropsFromWithEnrollment> & { preloadPrepaidModal?: boolean }>(
    ({ enrollmentAvailableChoices, preloadPrepaidModal }) =>
      enrollmentAvailableChoices.canEnrollThroughS12nPrepaid &&
      (paymentsExperiments.preview('preloadEnrollModalEnabled') || Boolean(preloadPrepaidModal)),
    branch<PropsFromCaller>(
      ({ isNotRequired }) => !!isNotRequired,
      withCoursesModulesDuration(true),
      withCoursesModulesDuration(false)
    )
  ),
  // All Enterprise enrollments need program and/or group info
  branch<Required<PropsFromWithEnrollment & PropsFromCaller>>(
    ({ enrollmentAvailableChoices }) =>
      enrollmentAvailableChoices.canEnrollThroughProgram ||
      enrollmentAvailableChoices.canEnrollThroughProgramInvitation ||
      enrollmentAvailableChoices.canEnrollThroughGroup,
    withProgramAndGroupData
  )
)(DataProvider);
